// 🔑 WITH / WITHOUT BANK-STATEMENT INCOME (Jesse, 2026-10-29 — push C item 5). Staff-only, read-only, on the Module's
// Income step: the decision engine run twice on the decision's own inputs (backend utils/bankStatementComparison —
// atlas priceLoan in its staffComparison mode, writing nothing). Renders NOTHING for a file without counted
// bank-statement income. Information only: it never changes the decision and is never shown to a borrower.
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { T, card, label } from './GuidedModuleSteps';

const money = (n) => (n == null || Number.isNaN(Number(n)) ? '—' : `$${Math.round(Number(n)).toLocaleString('en-US')}`);
const pct = (n, d = 1) => (n == null || Number.isNaN(Number(n)) ? '—' : `${(Number(n) * 100).toFixed(d)}%`);

const ROWS = [
  ['income', 'Qualifying income', (c) => `${money(c.grossMonthlyIncome)} / mo`],
  ['loan', 'Max loan', (c) => money(c.maxLoanAmount)],
  ['price', 'Max price', (c) => money(c.maxPurchasePrice)],
  ['ltv', 'LTV tier', (c) => `${pct(c.ltv, 0)}${c.ltvCapReason ? ` · ${c.ltvCapReason}` : ''}`],
  // No income left without the bank-statement income (backend noIncomeLeft): there is no ratio to show.
  ['dti', 'DTI', (c) => (c.dti == null && Number(c.grossMonthlyIncome) === 0 ? 'No income left' : `${pct(c.dti)} of ${pct(c.maxDti, 0)} max`)],
  ['eligible', 'Eligibility', (c) => (c.refused ? 'The engine would not decide' : c.eligible ? 'Eligible' : 'Not eligible')],
];

export default function BankStatementComparison({ API, borrowerId, headers, refreshKey = 0 }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let live = true;
    setErr(null);
    axios.get(`${API}/api/admin/borrowers/${borrowerId}/bank-statement-comparison`, { headers })
      .then((r) => { if (live) setData(r.data || null); })
      .catch((e) => { if (live) setErr(e?.response?.data?.error || 'The comparison could not be run just now.'); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API, borrowerId, refreshKey]);
  if (err) return <div data-testid="uw-bs-comparison-error" role="status" style={{ ...card(), fontSize: 14 }}>{err}</div>;
  if (!data || !data.applicable) return null;
  const cols = [['with', 'With bank-statement income', data.with], ['without', 'Without it', data.without]];
  const notes = (c) => (c.refused ? c.blockers : c.issues) || [];
  return (
    <div data-testid="uw-bs-comparison" style={card({ gap: 14 })}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8 }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>With and without bank-statement income</h2>
        <div style={label}>Staff only · read only</div>
      </div>
      <div style={{ fontSize: 14, lineHeight: 1.6 }}>
        {money(data.bankStatementMonthly)} / mo of the counted income is bank-statement income ({data.who || 'this file'}). Both columns are the
        decision engine on the decision&rsquo;s own inputs; the decision on file is not changed.
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${T.line}` }}>
              <th style={{ textAlign: 'left', padding: '8px', ...label }} />
              {cols.map(([k, title]) => <th key={k} style={{ textAlign: 'left', padding: '8px', ...label }}>{title}</th>)}
            </tr>
          </thead>
          <tbody>
            {ROWS.map(([key, name, fmt]) => (
              <tr key={key} data-testid={`uw-bs-comparison-row-${key}`} style={{ borderBottom: `1px solid ${T.line}` }}>
                <td style={{ padding: '10px 8px', fontWeight: 700 }}>{name}</td>
                {cols.map(([k, , c]) => (
                  <td key={k} data-testid={`uw-bs-comparison-${k}-${key}`} style={{ padding: '10px 8px' }}>{c ? fmt(c) : '—'}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {cols.some(([, , c]) => c && notes(c).length > 0) && (
        <div style={{ display: 'grid', gap: 6, fontSize: 13 }}>
          {cols.map(([k, title, c]) => (c && notes(c).length > 0 ? (
            <div key={k} data-testid={`uw-bs-comparison-${k}-reasons`}><b>{title}:</b> {notes(c).join('; ')}</div>
          ) : null))}
        </div>
      )}
    </div>
  );
}
